/**
 * JukeboxController - transport-driven snapshot cycling
 *
 * When jukebox mode is enabled and transport is playing, snapshots cycle
 * automatically every N bars. Starting transport resets to snapshot 0;
 * resuming from pause continues from the current snapshot.
 * User interaction (tweaking a param) pauses the jukebox;
 * saving or canceling resumes it.
 */

import type { ITransport, TransportCallback } from './ITransport';
import type { Snapshot, TweaksFile } from '../nodes/PresetManager';
import { logger } from '../logging';

export class JukeboxController {
  private transport: ITransport;
  private recallFn: (snapshot: Snapshot) => void;

  private _snapshots: Snapshot[] = [];
  private _snapshotBars = 4;
  private _jukeboxEnabled = false;
  private _activeIndex = 0;
  private _paused = false;
  private _dirty = false;
  private _wasPlaying = false;

  private scheduledEventId: number | null = null;

  private onStartBound: TransportCallback;
  private onStopBound: TransportCallback;
  private onPauseBound: TransportCallback;

  /** Callback invoked whenever jukebox state changes (for UI re-render) */
  onChange: (() => void) | null = null;

  constructor(transport: ITransport, recallFn: (snapshot: Snapshot) => void) {
    this.transport = transport;
    this.recallFn = recallFn;

    this.onStartBound = () => this.onTransportStart();
    this.onStopBound = () => this.onTransportStop();
    this.onPauseBound = () => this.onTransportPause();

    this.transport.on('start', this.onStartBound);
    this.transport.on('stop', this.onStopBound);
    this.transport.on('pause', this.onPauseBound);
  }

  // ─────────────────────────────────────────────────────────────
  // Getters
  // ─────────────────────────────────────────────────────────────

  get snapshots(): Snapshot[] { return this._snapshots; }
  get snapshotBars(): number { return this._snapshotBars; }
  get jukeboxEnabled(): boolean { return this._jukeboxEnabled; }
  get activeIndex(): number { return this._activeIndex; }
  get isDirty(): boolean { return this._dirty; }
  get isPaused(): boolean { return this._paused; }

  // ─────────────────────────────────────────────────────────────
  // Setters
  // ─────────────────────────────────────────────────────────────

  setJukeboxEnabled(on: boolean): void {
    this._jukeboxEnabled = on;
    if (on && this.transport.state === 'started' && !this._paused) {
      this.startScheduling();
    } else {
      this.stopScheduling();
    }
    this.emitChange();
  }

  setSnapshotBars(bars: number): void {
    this._snapshotBars = bars;
    // Restart scheduling with new interval if currently active
    if (this.scheduledEventId !== null) {
      this.stopScheduling();
      this.startScheduling();
    }
    this.emitChange();
  }

  // ─────────────────────────────────────────────────────────────
  // Snapshot management
  // ─────────────────────────────────────────────────────────────

  addSnapshot(snapshot: Snapshot): void {
    this._snapshots.push(snapshot);
    this.emitChange();
  }

  removeSnapshot(name: string): void {
    const idx = this._snapshots.findIndex(s => s.name === name);
    if (idx === -1) return;
    this._snapshots.splice(idx, 1);
    // Adjust active index
    if (this._activeIndex >= this._snapshots.length) {
      this._activeIndex = Math.max(0, this._snapshots.length - 1);
    }
    this.emitChange();
  }

  renameSnapshot(oldName: string, newName: string): void {
    const snapshot = this._snapshots.find(s => s.name === oldName);
    if (snapshot) {
      snapshot.name = newName;
      this.emitChange();
    }
  }

  recallByName(name: string): void {
    const idx = this._snapshots.findIndex(s => s.name === name);
    if (idx === -1) return;
    this._activeIndex = idx;
    this.recallFn(this._snapshots[idx]);
    this.emitChange();
  }

  recallByIndex(index: number): void {
    if (index < 0 || index >= this._snapshots.length) return;
    this._activeIndex = index;
    this.recallFn(this._snapshots[index]);
    this.emitChange();
  }

  // ─────────────────────────────────────────────────────────────
  // Dirty state (user tweaked a param)
  // ─────────────────────────────────────────────────────────────

  markDirty(): void {
    if (this._dirty || !this._jukeboxEnabled) return;
    this._dirty = true;
    this._paused = true;
    this.stopScheduling();
    this.emitChange();
  }

  /** Save: snapshot already added externally, resume from it */
  onSave(snapshotIndex: number): void {
    this._dirty = false;
    this._paused = false;
    this._activeIndex = snapshotIndex;
    if (this._jukeboxEnabled && this.transport.state === 'started') {
      this.startScheduling();
    }
    this.emitChange();
  }

  /** Cancel: revert to active snapshot, resume */
  onCancel(): void {
    if (this._snapshots.length > 0 && this._activeIndex < this._snapshots.length) {
      this.recallFn(this._snapshots[this._activeIndex]);
    }
    this._dirty = false;
    this._paused = false;
    if (this._jukeboxEnabled && this.transport.state === 'started') {
      this.startScheduling();
    }
    this.emitChange();
  }

  // ─────────────────────────────────────────────────────────────
  // Tweaks file serialization
  // ─────────────────────────────────────────────────────────────

  getTweaksFile(): TweaksFile {
    return {
      snapshots: this._snapshots,
      snapshotBars: this._snapshotBars,
      jukeboxEnabled: this._jukeboxEnabled,
    };
  }

  loadTweaksFile(tweaks: TweaksFile): void {
    this._snapshots = tweaks.snapshots ?? [];
    this._snapshotBars = tweaks.snapshotBars ?? 4;
    this._jukeboxEnabled = tweaks.jukeboxEnabled ?? false;
    this._activeIndex = 0;
    this._dirty = false;
    this._paused = false;
    this.stopScheduling();
    this.emitChange();
  }

  clear(): void {
    this._snapshots = [];
    this._snapshotBars = 4;
    this._jukeboxEnabled = false;
    this._activeIndex = 0;
    this._dirty = false;
    this._paused = false;
    this.stopScheduling();
    this.emitChange();
  }

  // ─────────────────────────────────────────────────────────────
  // Transport event handlers
  // ─────────────────────────────────────────────────────────────

  private onTransportStart(): void {
    if (!this._jukeboxEnabled || this._paused || this._snapshots.length === 0) return;

    // Fresh start (not resume from pause): reset to first snapshot
    if (!this._wasPlaying) {
      this._activeIndex = 0;
      this.recallFn(this._snapshots[0]);
    }
    this.startScheduling();
  }

  private onTransportStop(): void {
    this._wasPlaying = false;
    this.stopScheduling();
  }

  private onTransportPause(): void {
    this._wasPlaying = true;
    this.stopScheduling();
  }

  // ─────────────────────────────────────────────────────────────
  // Scheduling
  // ─────────────────────────────────────────────────────────────

  private startScheduling(): void {
    this.stopScheduling();
    if (this._snapshots.length <= 1) return;

    const interval = `${this._snapshotBars}m`;
    this.scheduledEventId = this.transport.scheduleRepeat(
      () => this.advanceSnapshot(),
      interval,
      interval  // first advance after one full cycle, not at time 0
    );
    logger.trace('JukeboxController', `Scheduling every ${interval}`);
  }

  private stopScheduling(): void {
    if (this.scheduledEventId !== null) {
      this.transport.clear(this.scheduledEventId);
      this.scheduledEventId = null;
    }
  }

  private advanceSnapshot(): void {
    if (this._snapshots.length === 0) return;
    this._activeIndex = (this._activeIndex + 1) % this._snapshots.length;
    this.recallFn(this._snapshots[this._activeIndex]);
    this.emitChange();
    logger.trace('JukeboxController', `Advanced to snapshot ${this._activeIndex}: ${this._snapshots[this._activeIndex].name}`);
  }

  private emitChange(): void {
    this.onChange?.();
  }

  dispose(): void {
    this.stopScheduling();
    this.transport.off('start', this.onStartBound);
    this.transport.off('stop', this.onStopBound);
    this.transport.off('pause', this.onPauseBound);
    this.onChange = null;
  }
}
